/**
 * StopMergeDialog — fusion assistée de deux POI aux coordonnées partagées.
 * Aucune fusion automatique : l'admin choisit l'arrêt absorbé, le nom conservé,
 * voit l'impact sur les inter-arrêts, puis confirme explicitement.
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ActionButton } from "@/components/ds";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { GitMerge, AlertTriangle } from "lucide-react";
import { formatCoord } from "@/lib/stopsUnits";

const FIELD = "w-full border border-zinc-900 px-3 py-2 text-sm bg-white";
const LABEL = "block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1";

function errText(e) {
  const d = e?.response?.data?.detail;
  return d && typeof d === "object" && !Array.isArray(d) && d.message ? d.message : formatApiErrorDetail(d);
}

export default function StopMergeDialog({ open, onClose, keep, stops, onMerged }) {
  const siblings = (stops || []).filter((s) => s.id !== keep?.id && s.latitude === keep?.latitude && s.longitude === keep?.longitude);
  const [mergeId, setMergeId] = useState("");
  const [name, setName] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const merge = siblings.find((s) => s.id === mergeId);

  useEffect(() => {
    if (open) { setMergeId(siblings.length === 1 ? siblings[0].id : ""); setName(keep?.name || ""); setPreview(null); setConfirm(false); }
  }, [open, keep?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !mergeId || !keep) { setPreview(null); return; }
    api.get(`/stops/${keep.id}/merge-preview`, { params: { merge_id: mergeId } })
      .then((r) => setPreview(r.data)).catch((e) => { toast.error(errText(e)); setPreview(null); });
  }, [open, mergeId, keep]);

  const doMerge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/stops/${keep.id}/merge`, { merge_id: mergeId, name: name.trim() || null });
      toast.success(`« ${merge.name} » fusionné dans « ${data.kept.name} »`);
      onMerged && onMerged(data);
      onClose();
    } catch (e) { toast.error(errText(e), { duration: 7000 }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border border-zinc-900 rounded-none p-0" data-testid="stop-merge-dialog">
        <DialogHeader className="bg-zinc-900 text-white px-4 py-3">
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <GitMerge size={16} className="text-[#FFC60A]" /> Fusionner deux POI
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Uniquement si MBC confirme qu'il s'agit du même arrêt physique. L'arrêt absorbé est supprimé, son nom conservé en alias, ses inter-arrêts re-pointés. Rien n'est fusionné sans votre confirmation.
          </DialogDescription>
        </DialogHeader>
        {keep && (
          <div className="p-4 space-y-3">
            <div className="text-xs border border-zinc-300 bg-zinc-50 p-2">
              <span className="font-mono uppercase tracking-widest text-zinc-500">Conservé</span> <b>{keep.name}</b> · <span className="font-mono">{formatCoord(keep.latitude)}, {formatCoord(keep.longitude)}</span>
            </div>
            <div>
              <label className={LABEL}>POI absorbé (mêmes coordonnées) *</label>
              <select data-testid="merge-select" className={FIELD} value={mergeId} onChange={(e) => setMergeId(e.target.value)}>
                <option value="">— choisir —</option>
                {siblings.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {siblings.length === 0 && <p className="text-xs text-orange-700 mt-1">Aucun autre POI aux mêmes coordonnées.</p>}
            </div>
            <div>
              <label className={LABEL}>Nom conservé *</label>
              <div className="flex gap-1 mb-1 flex-wrap">
                {[keep.name, merge?.name].filter(Boolean).map((n) => (
                  <button key={n} type="button" onClick={() => setName(n)} data-testid="merge-name-pick"
                    className={`px-2 py-0.5 text-[10px] font-mono border ${name === n ? "bg-zinc-900 text-[#FFC60A] border-zinc-900" : "border-zinc-300"}`}>{n}</button>
                ))}
              </div>
              <input data-testid="merge-name" className={FIELD} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            {preview && (
              <div className="text-xs space-y-1 border border-zinc-300 p-2" data-testid="merge-preview">
                <div>Inter-arrêts re-pointés vers l'arrêt conservé : <b>{preview.inter_stops_repoint.length}</b></div>
                <div>Tournées importées citant le nom absorbé : <b>{preview.tours_by_name}</b> (nom conservé en alias)</div>
                {preview.inter_stops_blocked.length > 0 && (
                  <div className="text-red-700 flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5" />
                    Fusion bloquée : {preview.inter_stops_blocked.map((b) => b.reason).join(" ; ")}</div>
                )}
              </div>
            )}
            <label className="flex items-start gap-2 text-xs">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="merge-confirm-check" />
              Je confirme que « {merge?.name || "…"} » et « {keep.name} » désignent le même arrêt physique (confirmation MBC).
            </label>
            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
              <ActionButton variant="ghost" onClick={onClose} testId="merge-cancel">Annuler</ActionButton>
              <ActionButton variant="danger" icon={GitMerge} onClick={doMerge} loading={busy} testId="merge-submit"
                disabled={!mergeId || !confirm || !name.trim() || !preview?.can_merge}>
                Fusionner
              </ActionButton>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
